<template>
  <div class="iso-edit-page">
    <!-- ===================== Hero ===================== -->
    <div class="iso-edit-hero">
      <div class="iso-edit-hero__blob iso-edit-hero__blob--one" />
      <div class="iso-edit-hero__blob iso-edit-hero__blob--two" />

      <div class="iso-edit-hero__content">
        <div class="iso-edit-hero__icon">
          <feather-icon
            icon="Edit3Icon"
            size="26"
          />
        </div>
        <div>
          <h2 class="iso-edit-hero__title">
            Edit ISO Templates
          </h2>
          <p class="iso-edit-hero__subtitle">
            Manage downloadable files for certificate group
            <strong>#{{ certificateId }}</strong>
          </p>
        </div>
      </div>

      <button
        type="button"
        class="iso-edit-back"
        @click="goBack"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          size="16"
          class="mr-50"
        />
        Back
      </button>
    </div>

    <!-- ===================== Loading ===================== -->
    <b-row v-if="pageLoading">
      <b-col
        v-for="n in 4"
        :key="n"
        cols="12"
        md="6"
      >
        <div class="iso-tpl-skel">
          <div class="iso-tpl-skel__line iso-tpl-skel__line--title" />
          <div class="iso-tpl-skel__row">
            <div class="iso-tpl-skel__icon" />
            <div class="iso-tpl-skel__btn" />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Empty ===================== -->
    <b-card
      v-else-if="!templatesIsoForm.length"
      class="iso-edit-shell text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="text-muted mb-1"
      />
      <h4>No templates found</h4>
      <p class="text-muted mb-0">
        This certificate group has no template slugs yet.
      </p>
    </b-card>

    <!-- ===================== Template cards ===================== -->
    <b-row
      v-else
      class="iso-tpl-grid"
    >
      <b-col
        v-for="template in templatesIsoForm"
        :key="template.slug"
        cols="12"
        md="6"
      >
        <div class="iso-tpl-card">
          <div class="iso-tpl-card__accent" />

          <!-- VIEW MODE -->
          <div
            v-if="!template.isEditing"
            class="iso-tpl-card__body"
          >
            <div class="iso-tpl-card__head">
              <div class="iso-tpl-card__icon-wrap">
                <feather-icon
                  icon="FileTextIcon"
                  size="22"
                />
              </div>
              <div class="iso-tpl-card__meta">
                <h4
                  class="iso-tpl-card__name"
                  :title="template.name"
                >
                  {{ template.name }}
                </h4>
                <span class="iso-tpl-card__slug">{{ template.slug }}</span>
              </div>
            </div>

            <div class="iso-tpl-card__actions">
              <button
                type="button"
                class="iso-tpl-btn iso-tpl-btn--download"
                :disabled="template.isLoadingFile"
                title="Download template"
                @click="downloadTemplate(template)"
              >
                <b-spinner
                  v-if="template.isLoadingFile"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="DownloadIcon"
                  size="15"
                  class="mr-50"
                />
                {{ template.isLoadingFile ? 'Downloading...' : 'Download' }}
              </button>

              <button
                type="button"
                class="iso-tpl-btn iso-tpl-btn--edit"
                @click="template.isEditing = true"
              >
                <feather-icon
                  icon="EditIcon"
                  size="15"
                  class="mr-50"
                />
                Edit
              </button>
            </div>
          </div>

          <!-- EDIT MODE -->
          <div
            v-else
            class="iso-tpl-card__body iso-tpl-card__body--edit"
          >
            <div class="iso-tpl-edit-label">
              <feather-icon
                icon="TypeIcon"
                size="14"
                class="mr-50"
              />
              Template Name
            </div>
            <b-form-input
              v-model="template.name"
              class="mb-1"
              placeholder="Template name"
            />

            <div class="iso-tpl-edit-label">
              <feather-icon
                icon="UploadIcon"
                size="14"
                class="mr-50"
              />
              Replace File
              <small class="text-muted ml-50">(optional)</small>
            </div>
            <b-form-file
              v-model="template.file"
              class="mb-1"
              accept=".pdf,.doc,.docx,.xlsx,.xls,.xlsm"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
            />

            <div class="iso-tpl-card__actions">
              <button
                v-if="!template.isChangeTemplate"
                type="button"
                class="iso-tpl-btn iso-tpl-btn--save"
                @click="editTemplatesIso(template)"
              >
                <feather-icon
                  icon="CheckIcon"
                  size="15"
                  class="mr-50"
                />
                Save
              </button>
              <button
                v-else
                type="button"
                class="iso-tpl-btn iso-tpl-btn--save"
                disabled
              >
                <b-spinner
                  small
                  type="grow"
                  class="mr-50"
                />
                Saving...
              </button>

              <button
                type="button"
                class="iso-tpl-btn iso-tpl-btn--cancel"
                :disabled="template.isChangeTemplate"
                @click="cancelEdit(template)"
              >
                <feather-icon
                  icon="XIcon"
                  size="15"
                  class="mr-50"
                />
                Cancel
              </button>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import axios from 'axios'
import {
  BRow, BCol, BCard, BFormInput, BFormFile, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BFormInput,
    BFormFile,
    BSpinner,
  },
  setup() {
    const templatesIsoForm = ref([])
    return {
      templatesIsoForm,
    }
  },
  data() {
    return {
      pageLoading: false,
    }
  },
  computed: {
    certificateId() {
      return this.$route.params.id
    },
  },
  mounted() {
    if (this.$route.params.id) {
      this.getTemplatesDetails()
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'iso-templates' })
    },

    getTemplatesDetails() {
      this.pageLoading = true
      axios
        .get(`iso/templates/${this.$route.params.id}`)
        .then(res => {
          const template = decryptData(res.data.payload).data.slugs || []
          this.templatesIsoForm = template.map(t => ({
            ...t,
            isEditing: false,
            file: null,
            isLoadingFile: false,
            isChangeTemplate: false,
            originalName: t.name,
          }))
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Failed to load templates',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.pageLoading = false
        })
    },

    cancelEdit(template) {
      // eslint-disable-next-line no-param-reassign
      template.name = template.originalName || template.name
      // eslint-disable-next-line no-param-reassign
      template.file = null
      // eslint-disable-next-line no-param-reassign
      template.isEditing = false
    },

    downloadTemplate(template) {
      // eslint-disable-next-line no-param-reassign
      template.isLoadingFile = true

      axios
        .get(`/iso/templates/${this.$route.params.id}/${template.slug}/download`, {
          responseType: 'blob',
        })
        .then(res => {
          let fileName = 'ISO Template'
          const contentDisposition = res.headers['content-disposition']

          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/)
            fileName = match ? match[1] : fileName
          }

          const blob = new Blob([res.data], {
            type: res.headers['content-type'],
          })

          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = fileName
          document.body.appendChild(link)
          link.click()
          link.remove()

          setTimeout(() => {
            window.URL.revokeObjectURL(url)
          }, 1000)
        })
        .catch(err => {
          if (err.response?.status === 404) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'File not found',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Download failed',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          }
        })
        .finally(() => {
          // eslint-disable-next-line no-param-reassign
          template.isLoadingFile = false
        })
    },

    editTemplatesIso(template) {
      if (!this.$route.params.id) return

      // eslint-disable-next-line no-param-reassign
      template.isChangeTemplate = true

      const formData = new FormData()
      formData.append('name', template.name)
      if (template.file) {
        formData.append('file', template.file)
      }

      axios
        .post(`iso/templates/${this.$route.params.id}/${template.slug}`, formData)
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Template updated successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.getTemplatesDetails()
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Failed to update template',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          // eslint-disable-next-line no-param-reassign
          template.isChangeTemplate = false
        })
    },
  },
}
</script>

<style lang="scss" scoped src="../EditIsoTemplate.scss"></style>
